import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const { login, isAuthenticated, setToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Only render if we're actually on the /login route
  const isLoginRoute = location.pathname === '/login' || location.pathname === '/auth/callback';

  // Watch location changes and hide immediately if not on login route
  useEffect(() => {
    if (location.pathname !== '/login' && location.pathname !== '/auth/callback') {
      setIsNavigating(true);
    }
    // Add/remove body class to help with CSS fallback
    if (location.pathname === '/login' || location.pathname === '/auth/callback') {
      document.body.classList.add('on-login-page');
    } else {
      document.body.classList.remove('on-login-page');
    }
    return () => {
      document.body.classList.remove('on-login-page');
    };
  }, [location.pathname]);

  useEffect(() => {
    if (isAuthenticated) {
      setIsNavigating(true);
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Handle SSO callback
  useEffect(() => {
    const token = searchParams.get('token');
    const userParam = searchParams.get('user');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      return;
    }

    if (token && userParam) {
      try {
        const user = JSON.parse(decodeURIComponent(userParam));
        setToken(token, user);
        setIsNavigating(true);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        setError('Failed to process SSO callback');
      }
    }
  }, [searchParams, setToken, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Immediately hide the component
      setIsNavigating(true);
      // Navigate immediately
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please check your email and password.');
      setLoading(false);
    }
  };

  // CRITICAL: Don't render login page if:
  // 1. Already authenticated
  // 2. Currently navigating away
  // 3. Not on the login route (check both isLoginRoute and location.pathname)
  // This prevents the overlay from appearing on other routes
  const shouldRender = isLoginRoute && 
                       !isAuthenticated && 
                       !isNavigating && 
                       (location.pathname === '/login' || location.pathname === '/auth/callback');
  
  if (!shouldRender) {
    return null;
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-slate-900 relative overflow-hidden" 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: shouldRender ? 1 : -9999,
        display: shouldRender ? 'flex' : 'none',
        pointerEvents: shouldRender ? 'auto' : 'none',
        visibility: shouldRender ? 'visible' : 'hidden'
      }}
    >
      <div className="relative z-10 max-w-md w-full mx-4">
        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl p-10 border border-white/20">
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-slate-800 rounded-xl flex items-center justify-center mb-6 shadow-lg border border-slate-700">
              <span className="text-white font-bold text-2xl">PS</span>
            </div>
            <h2 className="text-4xl font-extrabold text-gray-900 mb-2">
              Pulp Strategy CRM
            </h2>
            <p className="text-gray-600 font-medium">
              Sign in to access your dashboard
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm font-semibold text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-premium w-full"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-premium w-full"
                placeholder="Enter your password"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3.5 text-base"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            {/* Quick Access (Dev Only) removed */}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
